// src/design/registry/channelRegistry.ts
// Channel -> { Icon, color token, labelKey }. Reuses existing i18n keys.
// Single source of truth for channel labels and colours (Decision 12/13 of the
// hotel-operations redesign): every other channel label or colour lookup in
// the app must resolve through this registry.
import DvrIcon from '@mui/icons-material/Dvr';
import PublicIcon from '@mui/icons-material/Public';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import SendToMobileIcon from '@mui/icons-material/SendToMobile';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import type { Channel } from '../../types/models';
import type { VisualMeta } from './colors';

/**
 * Exact brand hex per channel (Decision 12, validated by Cris). These are the
 * ONLY hardcoded hex values allowed anywhere in the design registry — every
 * other colour must resolve against the active MUI theme.
 */
export const CHANNEL_HEX: Record<Channel | 'none', string> = {
  Kiosk: '#1499AD',
  AirLobby: '#B8862B',
  Online: '#4F6BD8',
  Dispenser: '#5E9E3C',
  MobileKiosk: '#9A5CC4',
  none: '#CDC7BB',
};

/** Long-form and short-form i18n keys, shared by every consumer (Decision 13). */
export const CHANNEL_LABEL_KEY: Record<Channel, string> = {
  Online: 'resources.channels.online',
  Kiosk: 'resources.channels.kiosk',
  AirLobby: 'resources.channels.airLobby',
  MobileKiosk: 'resources.channels.mobileKiosk',
  Dispenser: 'resources.channels.dispenser',
};

export const CHANNEL_SHORT_LABEL_KEY: Record<Channel, string> = {
  Online: 'resources.channels.short.online',
  Kiosk: 'resources.channels.short.kiosk',
  AirLobby: 'resources.channels.short.airLobby',
  MobileKiosk: 'resources.channels.short.mobileKiosk',
  Dispenser: 'resources.channels.short.dispenser',
};

export const CHANNEL_REGISTRY: Record<Channel, VisualMeta> = {
  Online:             { Icon: PublicIcon,       color: 'info',      labelKey: CHANNEL_LABEL_KEY.Online },
  Kiosk:              { Icon: DvrIcon,          color: 'primary',   labelKey: CHANNEL_LABEL_KEY.Kiosk },
  AirLobby:           { Icon: PhoneAndroidIcon, color: 'warning',   labelKey: CHANNEL_LABEL_KEY.AirLobby },
  MobileKiosk:        { Icon: SendToMobileIcon, color: 'secondary', labelKey: CHANNEL_LABEL_KEY.MobileKiosk },
  Dispenser:          { Icon: VpnKeyIcon,       color: 'success',   labelKey: CHANNEL_LABEL_KEY.Dispenser },
};

/** Resolve channel visual metadata; unknown channels fall back to a neutral icon + raw label (never silently coerced to Kiosk). */
export const getChannelMeta = (channel: string | null | undefined): VisualMeta => {
  if (!channel) return { Icon: HelpOutlineIcon, color: 'neutral', labelKey: '' };
  return CHANNEL_REGISTRY[channel as Channel] ?? { Icon: HelpOutlineIcon, color: 'neutral', labelKey: channel };
};

/** Short-form i18n key for chips/columns (Online · Air Lobby · Kiosco · Móvil · Dispensador). */
export const getChannelShortLabelKey = (channel: string | null | undefined): string => {
  if (!channel) return '';
  return CHANNEL_SHORT_LABEL_KEY[channel as Channel] ?? channel;
};

/** Exact brand hex for the channel (Decision 12). `null`/unknown -> the neutral "none" hex. */
export const getChannelHex = (channel: string | null | undefined): string =>
  (channel && CHANNEL_HEX[channel as Channel]) || CHANNEL_HEX.none;
